import { useState, useRef, useCallback } from "react";

const MIN_RECORDING_BYTES = 256;

function pickSupportedAudioMimeType() {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return undefined;
}

export function useVoiceRecorder(onRecordingStop, callbacks) {
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunks = useRef([]);
  const cancelledRef = useRef(false);
  const stoppingRef = useRef(false);
  const mimeTypeRef = useRef("audio/webm");
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const startRecording = useCallback(async () => {
    if (recording || isFinalizing || stoppingRef.current) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    cancelledRef.current = false;

    const mimeType = pickSupportedAudioMimeType();
    mimeTypeRef.current = mimeType ?? "audio/webm";
    const mediaRecorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;

    setRecordingTime(0);
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.current.push(e.data);
    };

    mediaRecorder.onstop = async () => {
      const recordedChunks = [...chunks.current];
      chunks.current = [];
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      const wasCancelled = cancelledRef.current;
      cancelledRef.current = false;

      try {
        if (wasCancelled) {
          callbacksRef.current?.onRecordingCancelled?.();
          return;
        }

        const blob = new Blob(recordedChunks, { type: mimeTypeRef.current });
        if (blob.size < MIN_RECORDING_BYTES) {
          callbacksRef.current?.onRecordingTooShort?.();
          return;
        }
        setIsFinalizing(true);
        await onRecordingStop(blob);
      } finally {
        stoppingRef.current = false;
        setIsFinalizing(false);
        mediaRecorderRef.current = null;
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };

    mediaRecorder.start();
    setRecording(true);
  }, [onRecordingStop, recording, isFinalizing]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || stoppingRef.current) return;
    if (recorder.state === "inactive") return;
    stoppingRef.current = true;
    recorder.stop();
    setRecording(false);
  }, []);

  const cancelRecording = useCallback(() => {
    if (stoppingRef.current && mediaRecorderRef.current?.state === "inactive") return;
    cancelledRef.current = true;
    stoppingRef.current = true;
    mediaRecorderRef.current?.stop();
    setRecording(false);
    setRecordingTime(0);
  }, []);

  return {
    recording,
    recordingTime,
    startRecording,
    stopRecording,
    cancelRecording,
    isFinalizing,
  };
}
